import { useEffect, useRef } from 'react';

import {
  drawGrid,
  drawTrace,
  drawYLabels,
  drawXLabels,
  drawTitle,
  drawMonitors,
  drawMarkers,
  drawMasks,
  handleResize,
  clearCanvas,
  drawThresholds,
  drawTimestamp,
  drawCrosspolMarker,
  drawTrendlineMarkers,
} from 'lib/lpt-charts/spectrumChart';

import { useDrawing } from './hooks/useDrawing';

import useBaselineTrace from 'features/spectrumChart/hooks/useBaselineTrace';
import { useAnalyzer } from 'features/analyzers/api/getAnalyzer';
import { useMinMax } from './hooks/useMinMax';
import { useTraceData } from './hooks/useTraceData';
import { useTimestamp } from './hooks/useTimestamp';
import { useFormatSnapshot } from './hooks/useSnapshot';
import { TraceHistory, TraceHistorySample } from 'types';
import { useChartLabels } from './hooks/useChartLabels';
import { InterferenceDetector } from 'types/shared/InterferenceDetector';
import useCrossPolData from 'features/spectrumChart/hooks/useCrossPolData';
import useMarkers from './hooks/useMarkers';
import useInLightMode from 'hooks/useInLightMode';
import {
  darkModeBaselineColor,
  darkModeCrossPolColor,
  getGridColor,
  lightModeBaselineColor,
  lightModeCrossPolColor,
  lightModeTraceColor,
} from './utils/colors';
import { useAppSelector } from 'app/hooks';

interface Props {
  analyzerId: number;
  snapshotId: number;
  drawingMode?: {
    isDrawing: boolean;
    drawingType: InterferenceDetector;
    redrawId: number;
    name?: string | undefined;
  };
  drawingFinished?: () => void;
  staticTrace?: number[] | undefined;
  staticSnapshot?: TraceHistory | TraceHistorySample | null | undefined;
  disableTraces?: boolean;
  hideLabels?: boolean;
  hideDetectorNames?: boolean;
  crossPolPeakSearch?: boolean;
}

const SpectrumChart = ({
  analyzerId,
  snapshotId,
  drawingMode,
  drawingFinished,
  staticTrace,
  staticSnapshot,
  disableTraces,
  hideLabels,
  hideDetectorNames,
  crossPolPeakSearch,
}: Props) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const callbackKeyRef = useRef<number>(-1);

  const inLightMode = useInLightMode();
  const { data: selectedAnalyzer } = useAnalyzer({ analyzerId });
  const snapshot = useFormatSnapshot({ snapshotId, staticSnapshot });
  const { scale, reference, monitors, masks, thresholds } = snapshot || {};
  const markers = useMarkers(snapshotId);
  const trendlineMode = useAppSelector((state) => state.markers.trendlineMode);

  const traceData = useTraceData({
    snapshotId,
    analyzerId,
    staticTrace,
  });

  const [snapshotFrequencies, xLabels, yLabels] = useChartLabels({
    snapshot,
    traceLength: traceData?.length || 601,
  });

  const [timestamp, timeWarningColor] = useTimestamp({
    initialTimestamp: snapshot?.last_updated,
    analyzer: selectedAnalyzer,
    snapshot: snapshot,
    inHistory: staticSnapshot !== undefined || staticTrace !== undefined,
  });

  const {
    drawingMouseDown,
    drawingMouseMove,
    drawingMouseUp,
    drawingMouseLeave,
    drawingMouseWheel,
    cancelDrawing,
    renderDrawing,
  } = useDrawing({
    canvas: canvasRef.current,
    drawingMode,
    drawingFinished: drawingFinished,
    snapshotId,
    snapshotFrequencies,
    centerUnit: snapshot?.center.split(' ')[1] || '',
    reference,
    scale,
    traceLength: traceData?.length,
  });

  const [maxTrace, minTrace] = useMinMax({
    snapshotId,
    initialMax: snapshot.max_trace,
    initialMin: snapshot.min_trace,
    minMaxEnabled: snapshot.minmax_enabled,
    traceData,
  });

  const formattedBaseline = useBaselineTrace(
    snapshot?.baseline_trace,
    snapshot?.baseline_enabled,
    false
  );

  const crossPolSnapshotData = useCrossPolData(snapshot?.crosspol_snapshot_id);
  const inCrosspolMode = crossPolSnapshotData.length > 0;
  const disableInterferenceDetectors = inCrosspolMode;

  useEffect(() => {
    cancelAnimationFrame(callbackKeyRef.current);

    const update = () => {
      const canvas: HTMLCanvasElement | null = canvasRef.current;
      const ctx: CanvasRenderingContext2D | null | undefined =
        canvas?.getContext('2d');
      if (!ctx) return;
      clearCanvas(ctx);
      drawTitle(ctx, `${selectedAnalyzer?.name || ''}  ${snapshot?.name}`);
      if (!hideLabels) {
        drawXLabels(ctx, xLabels);
        drawYLabels(ctx, yLabels);
      }
      drawGrid(ctx, getGridColor(inLightMode));
      if (snapshot?.id && snapshot?.id > 0 && !disableTraces) {
        const pixelTraceData = drawTrace(
          ctx,
          traceData || [],
          inLightMode ? lightModeTraceColor : '#ff0',
          reference,
          scale
        );
        drawTrace(ctx, maxTrace || [], 'red', reference, scale);
        drawTrace(ctx, minTrace || [], 'blue', reference, scale);
        drawTrace(
          ctx,
          formattedBaseline,
          inLightMode ? lightModeBaselineColor : darkModeBaselineColor,
          reference,
          scale
        );

        if (inCrosspolMode) {
          drawTrace(
            ctx,
            crossPolSnapshotData,
            inLightMode ? lightModeCrossPolColor : darkModeCrossPolColor,
            reference,
            scale
          );

          drawCrosspolMarker(
            ctx,
            traceData,
            reference,
            scale,
            0,
            crossPolPeakSearch
          );

          if (crossPolPeakSearch) {
            drawCrosspolMarker(
              ctx,
              crossPolSnapshotData,
              reference,
              scale,
              1,
              crossPolPeakSearch
            );
          }
        }

        if (!disableInterferenceDetectors) {
          drawMasks(
            ctx,
            masks,
            formattedBaseline,
            traceData,
            reference,
            scale,
            hideDetectorNames
          );
          drawMonitors(
            ctx,
            monitors,
            pixelTraceData,
            reference,
            scale,
            hideDetectorNames
          );
          drawThresholds(
            ctx,
            thresholds,
            traceData?.length,
            pixelTraceData,
            reference,
            scale,
            hideDetectorNames
          );
          drawMarkers(ctx, markers, traceData, reference, scale, false);

          if (trendlineMode) {
            drawTrendlineMarkers(
              ctx,
              markers,
              traceData,
              reference,
              scale,
              false
            );
          }
        }

        renderDrawing(traceData, formattedBaseline, pixelTraceData, true);
      }
      drawTimestamp(ctx, timestamp, timeWarningColor);
      callbackKeyRef.current = requestAnimationFrame(update);
    };
    update();
  });

  useEffect(() => {
    const canvas: HTMLCanvasElement | null = canvasRef.current;
    const ctx: CanvasRenderingContext2D | null | undefined =
      canvas?.getContext('2d');
    if (!ctx) return;
    handleResize(ctx);
    window.addEventListener('resize', () => handleResize(ctx));
    return () => {
      window.removeEventListener('resize', () => handleResize(ctx));
    };
  }, []);

  return (
    <div style={{ outline: 'none' }} tabIndex={0} onKeyDown={cancelDrawing}>
      <canvas
        ref={canvasRef}
        style={{ width: '100%', height: '100%' }}
        onMouseLeave={drawingMouseLeave}
        onMouseDown={drawingMouseDown}
        onMouseUp={drawingMouseUp}
        onMouseMove={drawingMouseMove}
        onWheel={drawingMouseWheel}
      />
    </div>
  );
};

export default SpectrumChart;
